/**
 * Client-side Upload Queue with Circuit Breaker
 * 
 * Manages upload concurrency, integrates with IndexedDB persistence,
 * and implements circuit breaker pattern for resilience.
 * 
 * ATOMIC UPLOAD: Storage upload + DB save happen together.
 * Upload is only marked "complete" after BOTH succeed.
 */

import { uploadWithTus, TusUploadResult } from './tusUploader';
import { 
  saveBlobsToIndexedDB,
  saveBlobsToIndexedDBWithId,
  updateBlobStatus,
  updateBlobPaths,
  deleteBlobFromIndexedDB,
  getPendingUpload,
  PendingUpload 
} from './blobStorage';
import { supabase } from '@/integrations/supabase/client';

// Re-export the type
export type { PendingUpload };

// Max concurrent uploads per browser tab
const MAX_CONCURRENT = 2;

// Circuit breaker settings
const CIRCUIT_BREAKER_THRESHOLD = 5; // failures to open circuit
const CIRCUIT_BREAKER_RESET_MS = 30000; // 30 seconds

// Hard timeout for entire upload item (video + audio combined)
const UPLOAD_ITEM_TIMEOUT_MS = 120000; // 2 minutes

// DB save retry settings (Layer 2)
const MAX_DB_RETRIES = 3;
const DB_RETRY_BASE_DELAY_MS = 1000; // 1 second, doubles each retry

export type UploadStatus = 'queued' | 'uploading' | 'complete' | 'failed' | 'paused';

export interface QueuedUpload {
  id: string; // IndexedDB record ID
  questionId: string;
  testResponseId: string;
  status: UploadStatus;
  progress: number;
  videoProgress: number;
  audioProgress: number;
  error?: string;
  videoPath?: string;
  audioPath?: string;
  createdAt: number;
}

export interface QueueState {
  uploads: QueuedUpload[];
  activeCount: number;
  queuedCount: number;
  isOnline: boolean;
  isPaused: boolean;
  circuitOpen: boolean;
}

type QueueListener = (state: QueueState) => void;
type CircuitBreakerListener = (isOpen: boolean, failureCount: number) => void;

class UploadQueue {
  private queue: Map<string, QueuedUpload> = new Map();
  private activeUploads = 0;
  private listeners: Set<QueueListener> = new Set();
  private circuitBreakerListeners: Set<CircuitBreakerListener> = new Set();
  private isPaused = false;
  private isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
  
  // Circuit breaker state
  private consecutiveFailures = 0;
  private circuitOpen = false;
  private circuitResetTimeout: ReturnType<typeof setTimeout> | null = null;

  // Track active abort controllers for cleanup
  private activeAbortControllers: Map<string, AbortController[]> = new Map();

  constructor() {
    // Listen for online/offline events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.isOnline = true;
        this.notifyListeners();
        this.processQueue();
      });
      
      window.addEventListener('offline', () => {
        this.isOnline = false;
        this.notifyListeners();
      });
    }
  }

  /**
   * Layer 1: Ensure we have a fresh, valid session token
   * Refreshes if token expires within 1 minute
   */
  private async ensureFreshSession(): Promise<boolean> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('[UploadQueue] Failed to get session:', error);
        return false;
      }
      
      if (!session) {
        console.error('[UploadQueue] No active session');
        return false;
      }
      
      // Check if token expires within 1 minute (60000ms)
      const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
      const expiresInMs = expiresAt - Date.now();
      
      if (expiresInMs < 60000) {
        console.log('[UploadQueue] Token expiring soon, refreshing session...');
        const { error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError) {
          console.error('[UploadQueue] Failed to refresh session:', refreshError);
          return false;
        }
        
        console.log('[UploadQueue] Session refreshed successfully');
      }
      
      return true;
    } catch (err) {
      console.error('[UploadQueue] Session validation error:', err);
      return false;
    }
  }

  /**
   * Layer 2: Save upload to database with retry logic and exponential backoff
   * Returns true if save succeeded, false otherwise
   */
  private async saveToDatabaseWithRetry(
    testResponseId: string,
    questionId: string,
    videoPath: string | null,
    audioPath: string | null
  ): Promise<{ success: boolean; error?: string }> {
    
    for (let attempt = 1; attempt <= MAX_DB_RETRIES; attempt++) {
      console.log(`[UploadQueue] DB save attempt ${attempt}/${MAX_DB_RETRIES} for question:`, questionId);
      
      // Refresh session before each attempt
      const sessionValid = await this.ensureFreshSession();
      if (!sessionValid) {
        console.warn(`[UploadQueue] Session invalid on attempt ${attempt}, will retry...`);
        if (attempt < MAX_DB_RETRIES) {
          const delay = DB_RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
          console.log(`[UploadQueue] Waiting ${delay}ms before retry...`);
          await new Promise(r => setTimeout(r, delay));
          continue;
        }
        return { success: false, error: 'Session validation failed after all retries' };
      }
      
      const { error: dbError } = await supabase
        .from('question_responses')
        .upsert(
          {
            test_response_id: testResponseId,
            question_id: questionId,
            response_content: videoPath,
            audio_url: audioPath,
            evaluation_status: 'pending',
            transcription_status: 'pending',
          } as any,
          {
            onConflict: 'test_response_id,question_id',
          }
        );
      
      if (!dbError) {
        console.log(`[UploadQueue] DB save successful on attempt ${attempt} for question:`, questionId);
        return { success: true };
      }
      
      console.error(`[UploadQueue] DB save attempt ${attempt} failed:`, dbError);
      
      // Don't retry on certain errors (e.g., RLS violations that won't change)
      if (dbError.code === '42501' || dbError.message?.includes('RLS')) {
        console.error('[UploadQueue] RLS error - not retrying');
        return { success: false, error: `Database permission error: ${dbError.message}` };
      }
      
      if (attempt < MAX_DB_RETRIES) {
        const delay = DB_RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1); // 1s, 2s, 4s
        console.log(`[UploadQueue] Waiting ${delay}ms before retry...`);
        await new Promise(r => setTimeout(r, delay));
      }
    }
    
    return { success: false, error: 'Database save failed after all retry attempts' };
  }

  /**
   * Enqueue a new upload - saves to IndexedDB first, then queues for upload
   */
  async enqueue(
    questionId: string,
    testResponseId: string,
    videoBlob?: Blob,
    audioBlob?: Blob,
    videoMimeType?: string,
    audioMimeType?: string
  ): Promise<string> {
    let id: string;

    // Step 1: Save to IndexedDB IMMEDIATELY (crash-safe)
    try {
      id = await saveBlobsToIndexedDB(
        questionId,
        testResponseId,
        videoBlob,
        audioBlob,
        videoMimeType,
        audioMimeType
      );
    } catch (indexedDBError) {
      console.error('[UploadQueue] Failed to save to IndexedDB:', indexedDBError);
      // Generate ID anyway and proceed - upload will work but won't be crash-safe
      id = crypto.randomUUID();
      console.warn('[UploadQueue] Proceeding without IndexedDB persistence');
    }

    // Step 2: Add to in-memory queue
    const queuedUpload: QueuedUpload = {
      id,
      questionId,
      testResponseId,
      status: 'queued',
      progress: 0,
      videoProgress: 0,
      audioProgress: 0,
      createdAt: Date.now(),
    };

    this.queue.set(id, queuedUpload);
    this.notifyListeners();

    // Step 3: Start processing
    this.processQueue();

    return id;
  }

  /**
   * Enqueue a new upload with a pre-generated ID
   * This prevents race conditions where fast uploads complete before the ID is returned
   */
  async enqueueWithId(
    id: string,
    questionId: string,
    testResponseId: string,
    videoBlob?: Blob,
    audioBlob?: Blob,
    videoMimeType?: string,
    audioMimeType?: string
  ): Promise<string> {
    // Step 1: Save to IndexedDB IMMEDIATELY (crash-safe) with provided ID
    try {
      await saveBlobsToIndexedDBWithId(
        id,
        questionId,
        testResponseId,
        videoBlob,
        audioBlob,
        videoMimeType,
        audioMimeType
      );
    } catch (indexedDBError) {
      console.error('[UploadQueue] Failed to save to IndexedDB:', indexedDBError);
      console.warn('[UploadQueue] Proceeding without IndexedDB persistence');
    }

    // Step 2: Add to in-memory queue
    const queuedUpload: QueuedUpload = {
      id,
      questionId,
      testResponseId,
      status: 'queued',
      progress: 0,
      videoProgress: 0,
      audioProgress: 0,
      createdAt: Date.now(),
    };

    this.queue.set(id, queuedUpload);
    this.notifyListeners();

    // Step 3: Start processing
    this.processQueue();

    return id;
  }

  /**
   * Re-queue a pending upload from IndexedDB (for recovery)
   */
  async requeueFromIndexedDB(pendingUpload: {
    id: string;
    questionId: string;
    testResponseId: string;
    status: string;
    createdAt: number;
    videoPath?: string;
    audioPath?: string;
  }): Promise<void> {
    // Don't re-queue if already in queue
    if (this.queue.has(pendingUpload.id)) {
      return;
    }

    const isUploaded = pendingUpload.status === 'uploaded';
    const hasPaths = pendingUpload.videoPath || pendingUpload.audioPath;

    const queuedUpload: QueuedUpload = {
      id: pendingUpload.id,
      questionId: pendingUpload.questionId,
      testResponseId: pendingUpload.testResponseId,
      // Only mark complete if uploaded AND we have paths
      status: (isUploaded && hasPaths) ? 'complete' : 'queued',
      progress: isUploaded ? 100 : 0,
      videoProgress: isUploaded ? 100 : 0,
      audioProgress: isUploaded ? 100 : 0,
      createdAt: pendingUpload.createdAt,
      // Restore paths from IndexedDB
      videoPath: pendingUpload.videoPath,
      audioPath: pendingUpload.audioPath,
    };

    this.queue.set(pendingUpload.id, queuedUpload);
    this.notifyListeners();

    // If not complete, re-process the queue to re-upload
    if (queuedUpload.status === 'queued') {
      console.log(`[UploadQueue] Re-queuing incomplete upload ${pendingUpload.id}`);
      this.processQueue();
    } else {
      console.log(`[UploadQueue] Recovered completed upload ${pendingUpload.id} with paths`);
    }
  }

  /**
   * Process the queue - upload next items if capacity available
   */
  private async processQueue(): Promise<void> {
    // Don't process if offline, paused, or circuit is open
    if (!this.isOnline || this.isPaused || this.circuitOpen) {
      return;
    }

    // Don't exceed concurrency limit
    if (this.activeUploads >= MAX_CONCURRENT) {
      return;
    }

    // Find next queued item
    const nextItem = Array.from(this.queue.values()).find(
      item => item.status === 'queued'
    );

    if (!nextItem) {
      return;
    }

    // Start upload
    this.activeUploads++;
    nextItem.status = 'uploading';
    this.notifyListeners();

    try {
      await this.uploadItem(nextItem);
      
      // Success - reset circuit breaker
      this.consecutiveFailures = 0;
      if (this.circuitOpen) {
        this.circuitOpen = false;
        this.notifyListeners();
        this.notifyCircuitBreakerListeners();
      }
      
    } catch (error) {
      console.error('[UploadQueue] Upload failed:', error);
      nextItem.status = 'failed';
      nextItem.error = error instanceof Error ? error.message : 'Upload failed';
      
      // Circuit breaker logic
      this.consecutiveFailures++;
      if (this.consecutiveFailures >= CIRCUIT_BREAKER_THRESHOLD) {
        this.openCircuit();
      }
    } finally {
      this.activeUploads--;
      // Clean up abort controllers for this item
      this.activeAbortControllers.delete(nextItem.id);
      this.notifyListeners();
      
      // Process next item
      this.processQueue();
    }
  }

  /**
   * Upload a single item (video + audio)
   */
  private async uploadItem(item: QueuedUpload): Promise<void> {
    let pending: PendingUpload | undefined;

    // Get blobs from IndexedDB with error handling
    try {
      pending = await getPendingUpload(item.id);
    } catch (err) {
      console.error('[UploadQueue] Failed to read from IndexedDB:', err);
      throw new Error('Failed to read upload data from storage');
    }

    if (!pending) {
      throw new Error('Pending upload not found in IndexedDB');
    }

    try {
      await updateBlobStatus(item.id, 'uploading');
    } catch (err) {
      console.warn('[UploadQueue] Failed to update IndexedDB status:', err);
      // Non-critical, continue with upload
    }

    // Layer 1: Validate session before starting uploads
    console.log('[UploadQueue] Validating session before upload...');
    const sessionValid = await this.ensureFreshSession();
    if (!sessionValid) {
      throw new Error('Session validation failed - please ensure you are logged in');
    }

    const uploadPromises: Promise<TusUploadResult>[] = [];
    const abortControllers: AbortController[] = [];
    const basePath = `${pending.testResponseId}/`;

    // Store abort controllers for this item
    this.activeAbortControllers.set(item.id, abortControllers);

    // Upload video if present
    if (pending.videoBlob) {
      const abortController = new AbortController();
      abortControllers.push(abortController);

      const videoExt = this.getExtension(pending.videoMimeType || 'video/webm');
      const videoPath = `${basePath}video_${crypto.randomUUID()}.${videoExt}`;
      
      uploadPromises.push(
        uploadWithTus({
          blob: pending.videoBlob,
          bucketName: 'test-responses',
          filePath: videoPath,
          contentType: pending.videoMimeType || 'video/webm',
          onProgress: (progress) => {
            item.videoProgress = progress;
            item.progress = this.calculateOverallProgress(item);
            this.notifyListeners();
          },
          abortSignal: abortController.signal,
        }).then(async result => {
          if (result.success && result.filePath) {
            item.videoPath = result.filePath;
            // Persist path to IndexedDB immediately for crash recovery
            try {
              await updateBlobPaths(item.id, result.filePath, undefined);
            } catch (err) {
              console.warn('[UploadQueue] Failed to persist video path:', err);
            }
          }
          return result;
        })
      );
    }

    // Upload audio if present
    if (pending.audioBlob) {
      const abortController = new AbortController();
      abortControllers.push(abortController);

      const audioExt = this.getExtension(pending.audioMimeType || 'audio/webm');
      const audioPath = `${basePath}audio_${crypto.randomUUID()}.${audioExt}`;
      
      uploadPromises.push(
        uploadWithTus({
          blob: pending.audioBlob,
          bucketName: 'test-responses',
          filePath: audioPath,
          contentType: pending.audioMimeType || 'audio/webm',
          onProgress: (progress) => {
            item.audioProgress = progress;
            item.progress = this.calculateOverallProgress(item);
            this.notifyListeners();
          },
          abortSignal: abortController.signal,
        }).then(async result => {
          if (result.success && result.filePath) {
            item.audioPath = result.filePath;
            // Persist path to IndexedDB immediately for crash recovery
            try {
              await updateBlobPaths(item.id, undefined, result.filePath);
            } catch (err) {
              console.warn('[UploadQueue] Failed to persist audio path:', err);
            }
          }
          return result;
        })
      );
    }

    // Create timeout promise for hard limit
    let timeoutId: ReturnType<typeof setTimeout>;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        // Abort all uploads when timeout fires
        console.warn('[UploadQueue] Timeout reached, aborting uploads for:', item.id);
        abortControllers.forEach(controller => controller.abort());
        reject(new Error('Upload timeout - exceeded 2 minute limit'));
      }, UPLOAD_ITEM_TIMEOUT_MS);
    });

    try {
      // Wait for all uploads with hard timeout
      console.log('[UploadQueue] Starting upload with 2-minute hard timeout:', item.id);
      const results = await Promise.race([
        Promise.all(uploadPromises),
        timeoutPromise
      ]) as TusUploadResult[];

      // Clear timeout if uploads completed successfully
      clearTimeout(timeoutId!);

      // Check for failures
      const failed = results.find(r => !r.success);
      if (failed) {
        throw failed.error || new Error('Upload failed');
      }

      // ATOMIC: Now save to database with retry logic (Layer 2)
      console.log('[UploadQueue] Storage upload complete, saving to database with retry:', {
        testResponseId: item.testResponseId,
        questionId: item.questionId,
        videoPath: item.videoPath,
        audioPath: item.audioPath,
      });

      const dbResult = await this.saveToDatabaseWithRetry(
        item.testResponseId,
        item.questionId,
        item.videoPath || null,
        item.audioPath || null
      );

      if (!dbResult.success) {
        // Mark as failed - storage succeeded but DB failed after all retries
        throw new Error(dbResult.error || 'Database save failed after retries');
      }

      console.log('[UploadQueue] Database save successful for question:', item.questionId);

      // Mark as uploaded in IndexedDB
      try {
        await updateBlobStatus(item.id, 'uploaded');
      } catch (err) {
        console.warn('[UploadQueue] Failed to update IndexedDB status to uploaded:', err);
      }
      item.status = 'complete';
      item.progress = 100;
    } catch (error) {
      clearTimeout(timeoutId!);
      throw error;
    }
  }

  private calculateOverallProgress(item: QueuedUpload): number {
    const videoWeight = item.videoPath !== undefined || item.videoProgress > 0 ? 0.8 : 0;
    const audioWeight = item.audioPath !== undefined || item.audioProgress > 0 ? 0.2 : 0;
    
    if (videoWeight === 0 && audioWeight === 0) return 0;
    
    const normalizedVideoWeight = videoWeight / (videoWeight + audioWeight);
    const normalizedAudioWeight = audioWeight / (videoWeight + audioWeight);
    
    return Math.round(
      item.videoProgress * normalizedVideoWeight +
      item.audioProgress * normalizedAudioWeight
    );
  }

  private getExtension(mimeType: string): string {
    if (mimeType.includes('mp4')) return 'mp4';
    if (mimeType.includes('ogg')) return 'ogg';
    if (mimeType.includes('aac')) return 'aac';
    return 'webm';
  }

  /**
   * Open the circuit breaker
   */
  private openCircuit(): void {
    console.warn('[UploadQueue] Circuit breaker OPEN - too many failures');
    this.circuitOpen = true;
    this.notifyListeners();
    this.notifyCircuitBreakerListeners();

    // Auto-reset after timeout
    if (this.circuitResetTimeout) {
      clearTimeout(this.circuitResetTimeout);
    }
    
    this.circuitResetTimeout = setTimeout(() => {
      console.log('[UploadQueue] Circuit breaker RESET');
      this.circuitOpen = false;
      this.consecutiveFailures = 0;
      this.notifyListeners();
      this.notifyCircuitBreakerListeners();
      this.processQueue();
    }, CIRCUIT_BREAKER_RESET_MS);
  }

  /**
   * Pause all uploads
   */
  pause(): void {
    this.isPaused = true;
    this.notifyListeners();
  }

  /**
   * Resume uploads
   */
  resume(): void {
    this.isPaused = false;
    this.notifyListeners();
    this.processQueue();
  }

  /**
   * Remove all uploads for a question (for re-recording)
   * This aborts any active uploads and removes from queue
   */
  removeByQuestionId(questionId: string): void {
    const uploadsToRemove: string[] = [];
    
    this.queue.forEach((upload, id) => {
      if (upload.questionId === questionId) {
        uploadsToRemove.push(id);
        
        // Abort any active uploads for this item
        const controllers = this.activeAbortControllers.get(id);
        if (controllers) {
          controllers.forEach(c => c.abort());
          this.activeAbortControllers.delete(id);
        }
      }
    });
    
    uploadsToRemove.forEach(id => {
      const upload = this.queue.get(id);
      if (upload?.status === 'uploading') {
        this.activeUploads--;
      }
      this.queue.delete(id);
    });
    
    if (uploadsToRemove.length > 0) {
      console.log(`[UploadQueue] Removed ${uploadsToRemove.length} uploads for question ${questionId}`);
      this.notifyListeners();
    }
  }

  /**
   * Retry a failed upload
   */
  retry(id: string): void {
    const item = this.queue.get(id);
    if (item && item.status === 'failed') {
      item.status = 'queued';
      item.progress = 0;
      item.videoProgress = 0;
      item.audioProgress = 0;
      item.error = undefined;
      this.notifyListeners();
      this.processQueue();
    }
  }

  /**
   * Force finalize an upload - save whatever we have and close it out.
   * Used as last resort when user wants to proceed despite upload issues.
   *
   * @returns Object with success status and what was saved
   */
  async forceFinalize(id: string): Promise<{
    success: boolean;
    savedToDb: boolean;
    hasVideoPath: boolean;
    hasAudioPath: boolean;
    error?: string;
  }> {
    const item = this.queue.get(id);

    if (!item) {
      return {
        success: false,
        savedToDb: false,
        hasVideoPath: false,
        hasAudioPath: false,
        error: 'Upload not found in queue'
      };
    }

    console.log('[UploadQueue] Force finalizing upload:', id, {
      currentStatus: item.status,
      videoPath: item.videoPath,
      audioPath: item.audioPath,
      progress: item.progress,
    });

    // Step 1: Abort any active TUS uploads for this item
    const controllers = this.activeAbortControllers.get(id);
    if (controllers) {
      controllers.forEach(controller => {
        try {
          controller.abort();
        } catch (e) {
          console.warn('[UploadQueue] Error aborting controller:', e);
        }
      });
      this.activeAbortControllers.delete(id);
    }

    // Step 2: Check if we have storage paths (completed uploads)
    const hasVideoPath = !!item.videoPath;
    const hasAudioPath = !!item.audioPath;

    // Step 3: If we have ANY paths, attempt DB save
    let savedToDb = false;
    if (hasVideoPath || hasAudioPath) {
      try {
        // Use existing retry logic
        const dbResult = await this.saveToDatabaseWithRetry(
          item.testResponseId,
          item.questionId,
          item.videoPath || null,
          item.audioPath || null
        );

        savedToDb = dbResult.success;
        
        if (!savedToDb) {
          console.error('[UploadQueue] Force finalize DB save failed:', dbResult.error);
        } else {
          console.log('[UploadQueue] Force finalize DB save successful');
        }
      } catch (err) {
        console.error('[UploadQueue] Force finalize DB error:', err);
      }
    }

    // Step 4: Update queue state
    if (item.status === 'uploading') {
      this.activeUploads--;
    }

    // Mark as complete if we saved to DB, otherwise mark as failed
    item.status = savedToDb ? 'complete' : 'failed';
    item.progress = savedToDb ? 100 : item.progress;
    if (!savedToDb && !hasVideoPath && !hasAudioPath) {
      item.error = 'Force closed - no data uploaded';
    }

    // Step 5: Clean up IndexedDB if successful
    if (savedToDb) {
      try {
        await updateBlobStatus(id, 'uploaded');
        await deleteBlobFromIndexedDB(id);
      } catch (err) {
        console.warn('[UploadQueue] Force finalize IndexedDB cleanup error:', err);
      }
    }

    this.notifyListeners();

    // Success = saved something to DB OR there was nothing to save (blank response)
    return {
      success: savedToDb || (!hasVideoPath && !hasAudioPath),
      savedToDb,
      hasVideoPath,
      hasAudioPath,
    };
  }

  /**
   * Mark an upload as fully saved (database confirmed)
   * This triggers IndexedDB cleanup
   */
  async markAsSaved(id: string): Promise<void> {
    try {
      await deleteBlobFromIndexedDB(id);
    } catch (err) {
      console.warn('[UploadQueue] Failed to delete from IndexedDB:', err);
    }
    this.queue.delete(id);
    this.notifyListeners();
  }

  /**
   * Get current upload by ID
   */
  getUpload(id: string): QueuedUpload | undefined {
    return this.queue.get(id);
  }

  /**
   * Get upload by question ID
   */
  getUploadByQuestionId(questionId: string): QueuedUpload | undefined {
    return Array.from(this.queue.values()).find(u => u.questionId === questionId);
  }

  /**
   * Subscribe to queue state changes
   */
  subscribe(listener: QueueListener): () => void {
    this.listeners.add(listener);
    // Immediately notify with current state
    listener(this.getState());
    return () => this.listeners.delete(listener);
  }

  /**
   * Subscribe to circuit breaker state changes
   */
  subscribeToCircuitBreaker(listener: CircuitBreakerListener): () => void {
    this.circuitBreakerListeners.add(listener);
    // Immediately notify with current state
    listener(this.circuitOpen, this.consecutiveFailures);
    return () => this.circuitBreakerListeners.delete(listener);
  }

  private notifyCircuitBreakerListeners(): void {
    this.circuitBreakerListeners.forEach(listener =>
      listener(this.circuitOpen, this.consecutiveFailures)
    );
  }

  /**
   * Get current queue state
   */
  getState(): QueueState {
    const uploads = Array.from(this.queue.values());
    return {
      uploads,
      activeCount: uploads.filter(u => u.status === 'uploading').length,
      queuedCount: uploads.filter(u => u.status === 'queued').length,
      isOnline: this.isOnline,
      isPaused: this.isPaused,
      circuitOpen: this.circuitOpen,
    };
  }

  /**
   * Check if there are pending uploads
   */
  hasPendingUploads(): boolean {
    return Array.from(this.queue.values()).some(
      u => u.status === 'queued' || u.status === 'uploading'
    );
  }

  /**
   * Get count of pending uploads
   */
  getPendingCount(): number {
    return Array.from(this.queue.values()).filter(
      u => u.status === 'queued' || u.status === 'uploading'
    ).length;
  }

  private notifyListeners(): void {
    const state = this.getState();
    this.listeners.forEach(listener => listener(state));
  }
}

// Singleton instance
export const uploadQueue = new UploadQueue();
